import type { MessageView } from "@/lib/types";
import EmailReviewer from "@/components/EmailReviewer";
import EmailBodyView from "@/components/EmailBodyView";
import { EmailStatusBadge, InjectionBadge } from "@/components/EmailStatusBadge";
import { formatDateTime } from "@/lib/format";
import { cn } from "@/lib/cn";

export type BubbleState = "collapsed" | "body" | "review";

/** When a message happened, as the reader would date it: sent, else received, else composed. */
export function messageStamp(m: MessageView): string {
  return m.sentAt ?? m.receivedAt ?? m.createdAt;
}

/**
 * How a message is tinted, so direction is readable at a glance rather than by
 * reading the label. Three cues carry it together — an indent, a coloured rail
 * down the edge, and a fill — because the fills alone are near-white and a
 * scanning eye does not resolve them side by side. Colour is never the only
 * cue: every message also keeps its "Us"/"Them" or Outbound/Inbound wording.
 */
function tone(m: MessageView): { frame: string; label: string; who: string } {
  if (m.direction === "OUTBOUND" && m.status === "DRAFT") {
    // Awaiting a human. It is ours, so it keeps the outbound indent, but the
    // amber outranks the blue — it is the one message on the page to act on.
    return {
      frame: "ml-6 border-l-4 border-l-amber-500 border-amber-200 bg-amber-50",
      label: "text-amber-700",
      who: "Us",
    };
  }
  if (m.direction === "OUTBOUND") {
    return {
      frame: "ml-6 border-l-4 border-l-[var(--primary)] border-blue-200 bg-blue-50",
      label: "text-[var(--primary)]",
      who: "Us",
    };
  }
  return {
    frame: "mr-6 border-l-4 border-l-zinc-500 border-[var(--border)] bg-zinc-100",
    label: "text-zinc-700",
    who: "Them",
  };
}

/**
 * One message of a conversation, at one of three depths: a one-line summary
 * for history, the message as its recipient sees it, or the full reviewer —
 * where approving, editing, rejecting and capturing training feedback live.
 *
 * <p>Shared by every place a thread is laid out — the prospect's conversation
 * and the history under a message opened from the oversight list — so a
 * message looks the same, and reads the same way, wherever an operator meets it.
 */
export default function MessageBubble({
  m,
  state,
  onExpand,
  onToggleReview,
}: {
  m: MessageView;
  state: BubbleState;
  onExpand: () => void;
  /** Asked for the reviewer (or, when it is open, to close it again). */
  onToggleReview: () => void;
}) {
  const outbound = m.direction === "OUTBOUND";
  const t = tone(m);

  if (state === "collapsed") {
    return (
      <button
        type="button"
        onClick={onExpand}
        className={cn(
          "flex w-full items-baseline gap-3 rounded-md border px-3 py-2 text-left hover:brightness-95",
          t.frame,
        )}
      >
        <span className={cn("shrink-0 text-xs font-semibold", t.label)}>{t.who}</span>
        <span className="truncate text-sm">
          <span className="font-medium">{m.subject}</span>
          <span className="text-[var(--fg-muted)]"> — {preview(m.bodyText)}</span>
        </span>
        <span className="ml-auto shrink-0 text-xs text-[var(--fg-muted)]">
          {formatDateTime(messageStamp(m))}
        </span>
      </button>
    );
  }

  return (
    <div className={cn("rounded-md border p-3", t.frame)}>
      {/* The reviewer carries its own header — status, direction, subject,
          timestamp — so repeating one above it would say everything twice. */}
      {state === "review" ? (
        <>
          {/* The reviewer does not carry this one, and it is the reason the
              agent's answer to this message needed a human at all. */}
          {m.injectionSuspected ? (
            <div className="mb-2">
              <InjectionBadge />
            </div>
          ) : null}
          <EmailReviewer emailId={m.id} />
        </>
      ) : (
        <>
          <div className="mb-1 flex items-center justify-between gap-2 text-xs text-[var(--fg-muted)]">
            <span className="flex flex-wrap items-center gap-2">
              <span className={cn("font-semibold uppercase tracking-wide", t.label)}>
                {outbound ? "Outbound" : "Inbound"}
              </span>
              <EmailStatusBadge status={m.status} />
              {m.injectionSuspected ? <InjectionBadge /> : null}
            </span>
            <span className="shrink-0">{formatDateTime(messageStamp(m))}</span>
          </div>
          <div className="mb-1 text-sm font-medium">{m.subject}</div>
          <EmailBodyView bodyText={m.bodyText} bodyHtml={m.bodyHtml} className="text-zinc-800" />
        </>
      )}
      <button
        type="button"
        onClick={onToggleReview}
        className="mt-2 text-xs text-[var(--primary)] hover:underline"
      >
        {state === "review" ? "Collapse" : "Review · feedback · actions"}
      </button>
    </div>
  );
}

function preview(bodyText: string): string {
  const flat = bodyText.replace(/\s+/g, " ").trim();
  return flat.length > 120 ? `${flat.slice(0, 120)}…` : flat;
}
